/**
 *  @class
 *  @function LocalizationForm
 */
if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.inputs = this.form.querySelectorAll('select[name="locale_code"], select[name="country_code"]');
      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 200);
      this.inputs.forEach((item) => item.addEventListener('change', this.debouncedOnSubmit.bind(this)));
      this.setupLanguageFlags();
    }

    setupLanguageFlags() {
      const wrap = this.querySelector('[data-thb-language-flags]');
      if (!wrap) return;

      const select = wrap.querySelector('select.thb-language-code');
      const toggle = wrap.querySelector('.thb-language-flag-toggle');
      const dropdown = wrap.querySelector('.thb-language-flag-dropdown');
      const toggleFlag = wrap.querySelector('.thb-language-flag-toggle__flag');
      const toggleName = wrap.querySelector('.thb-language-flag-toggle__name');
      const buttons = wrap.querySelectorAll('.thb-language-flag-btn');
      if (!select || !toggle) return;

      const isStatic = toggle.classList.contains('thb-language-flag-toggle--static');
      if (isStatic || !dropdown || !buttons.length) {
        select.setAttribute('tabindex', '-1');
        return;
      }

      select.setAttribute('tabindex', '-1');

      const labelPrefix = wrap.dataset.langLabelPrefix || '';

      const close = () => {
        dropdown.hidden = true;
        toggle.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      };

      const open = () => {
        dropdown.hidden = false;
        toggle.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      };

      const isOpen = () => toggle.getAttribute('aria-expanded') === 'true';

      const updateToggleAria = () => {
        const activeBtn = wrap.querySelector('.thb-language-flag-btn.is-active');
        const name = activeBtn?.getAttribute('aria-label');
        if (name && labelPrefix) {
          toggle.setAttribute('aria-label', `${labelPrefix}: ${name}`);
        }
      };

      const syncFromSelect = () => {
        const val = select.value;
        buttons.forEach((btn) => {
          const active = btn.dataset.locale === val;
          btn.classList.toggle('is-active', active);
          if (active) {
            btn.setAttribute('aria-current', 'true');
          } else {
            btn.removeAttribute('aria-current');
          }
        });
        const activeBtn = wrap.querySelector('.thb-language-flag-btn.is-active');
        if (activeBtn && toggleFlag) {
          const flagEl = activeBtn.querySelector('.thb-language-flag-btn__flag');
          toggleFlag.innerHTML = flagEl ? flagEl.innerHTML : activeBtn.innerHTML;
        }
        if (activeBtn && toggleName) {
          const nameEl = activeBtn.querySelector('.thb-language-flag-btn__name');
          toggleName.textContent = nameEl ? nameEl.textContent : activeBtn.getAttribute('aria-label') || '';
        }
        updateToggleAria();
      };

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        if (isOpen()) {
          close();
        } else {
          open();
        }
      });

      this._langCloseOnDoc = (e) => {
        if (!isOpen()) return;
        if (!wrap.contains(e.target)) close();
      };
      this._langCloseOnEscape = (e) => {
        if (e.key === 'Escape' && isOpen()) {
          close();
          toggle.focus();
        }
      };

      document.addEventListener('click', this._langCloseOnDoc);
      document.addEventListener('keydown', this._langCloseOnEscape);

      buttons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          const next = btn.dataset.locale;
          close();
          if (!next || select.value === next) return;
          select.value = next;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          syncFromSelect();
        });
      });

      select.addEventListener('change', syncFromSelect);
      syncFromSelect();
    }

    disconnectedCallback() {
      if (this._langCloseOnDoc) document.removeEventListener('click', this._langCloseOnDoc);
      if (this._langCloseOnEscape) document.removeEventListener('keydown', this._langCloseOnEscape);
    }

    onSubmitHandler(event) {
      if (this.form) this.form.submit();
    }
  }
  customElements.define('localization-form', LocalizationForm);
}
